import {Show} from 'platform/foundation';
import {useTheme} from 'styled-components';

import {getSafely, suffixTestId, TestIdProps} from 'shared/web';

export interface QuintileZone {
  boundaryX: number;
  labelX: number;
  hasSeparator: boolean;
  sharePercent: number;
  rangeLabel: string;
}

interface QuintileZonesProps extends TestIdProps {
  quintiles: QuintileZone[];
  /** Pixel width of one fifth of the chart. */
  zoneWidth: number;
  /** Top edge of the tinted zone area. */
  zoneTopY: number;
  baselineY: number;
  /** Pastel green→red fills, one per fifth. */
  zoneFills: string[];
  fallbackFill: string;
}

/** Linear-axis backgrounds: five tinted fifths with separators and share labels. */
export function QuintileZones(props: QuintileZonesProps) {
  const theme = useTheme();

  return (
    <g data-testid={suffixTestId('quintileZones', props)}>
      {props.quintiles.map((quintile, fifth) => (
        <g key={quintile.boundaryX}>
          <rect
            x={quintile.boundaryX}
            y={props.zoneTopY}
            width={props.zoneWidth}
            height={props.baselineY - props.zoneTopY}
            fill={getSafely(props.zoneFills, fifth) ?? props.fallbackFill}
          />
          <Show when={quintile.hasSeparator}>
            <line
              x1={quintile.boundaryX}
              y1={props.zoneTopY}
              x2={quintile.boundaryX}
              y2={props.baselineY}
              stroke={theme.colors.palettes.neutral[40][100]}
              strokeDasharray="4 3"
            />
          </Show>
          <text
            x={quintile.labelX}
            y={props.zoneTopY - 2}
            fontSize={theme.fontSizes.text.xSmall}
            fontWeight={theme.fontWeights.text.alternative}
            fill={theme.colors.text.primary}
            textAnchor="middle"
          >
            {`${quintile.sharePercent} %`}
          </text>
          <text
            x={quintile.labelX}
            y={props.zoneTopY + 12}
            fontSize={theme.fontSizes.text.xxSmall}
            fontWeight={theme.fontWeights.text.default}
            fill={theme.colors.text.tertiary}
            textAnchor="middle"
          >
            {quintile.rangeLabel}
          </text>
        </g>
      ))}
    </g>
  );
}
